// =============================================================================
// Community Guidelines — structured content by language.
// English is canonical (mirrors COMMUNITY_GUIDELINES.md in docs/legal/ of the parent triptribe repo).
// Translations must be updated whenever the English source changes.
// See ./types.ts for the shared shape and fallback behavior.
//
// Not translated anywhere: Tonyte, Partner Dashboard, hello@tonyte.app,
// "Terms of Service, Section 9" (cross-reference — same section number in
// every language).
// =============================================================================

import type { LegalContentByLanguage } from './types';

export const communityGuidelinesContent: LegalContentByLanguage = {
  en: {
    title: 'Community Guidelines',
    subtitle:
      'What we expect from everyone using Tonyte, in plain language. These Guidelines are part of our Terms of Service.',
    iconName: 'users',
    sections: [
      {
        title: 'Introduction',
        iconName: 'info',
        paragraphs: [
          "Tonyte exists to help you find real events, real plans, and real people worth spending an evening with. That only works if the community feels safe, genuine, and welcoming — so here's what we expect from everyone using Tonyte, in plain language.",
          'These Guidelines are part of our Terms of Service — breaking them is breaking the Terms, and the enforcement steps at the end of this document apply.',
        ],
      },
      {
        title: 'Be Respectful',
        iconName: 'heart',
        paragraphs: [
          "Treat other people the way you'd want to be treated at an event you're excited about. Disagreements happen; cruelty doesn't need to. Don't demean, threaten, or deliberately upset other users — in chat, on a profile, at an event, or anywhere else Tonyte connects people.",
        ],
      },
      {
        title: 'Be Authentic',
        iconName: 'user-check',
        paragraphs: [
          "Your profile should reflect the real you — honest photos, accurate details, and a genuine bio. If you're organizing or attending an event, keep the information you share truthful: real times, real locations, real intentions. Authenticity is what makes Tonyte work, so bring it to everything you post.",
        ],
      },
      {
        title: 'No Harassment',
        iconName: 'slash',
        paragraphs: [
          "Repeated unwanted contact, following someone from event to event to make them uncomfortable, sending unwanted explicit messages, or targeting someone after they've asked you to stop are all harassment, and they're not welcome here. If someone blocks you or asks you to stop, stop.",
        ],
      },
      {
        title: 'No Hate Speech',
        iconName: 'alert-triangle',
        paragraphs: [
          'Content that attacks, demeans, or incites hostility against someone based on race, ethnicity, national origin, religion, disability, age, sexual orientation, gender identity, or any other protected characteristic has no place on Tonyte, in any form — profile content, chat, event descriptions, or anything else.',
        ],
      },
      {
        title: 'No Spam',
        iconName: 'x-circle',
        paragraphs: [
          "Don't use Tonyte to mass-message people you don't know, flood chats or event listings with repetitive content, or use the platform primarily to promote something unrelated to genuinely connecting with people or discovering events. This includes commercial advertising, unsolicited promotions, referral or affiliate schemes, and repeated promotional messaging that falls outside legitimate Partner activity on the Partner Dashboard. If it feels like you're broadcasting instead of connecting, it's probably spam.",
        ],
      },
      {
        title: 'No Fake Events',
        iconName: 'calendar',
        paragraphs: [
          "Every event on Tonyte should be a real thing, actually happening, at the time and place listed. Don't post events that don't exist, that you have no intention of holding, or that are designed to lure people somewhere under false pretenses. This applies to every user and every Partner organizer alike.",
        ],
      },
      {
        title: 'No Impersonation',
        iconName: 'user-x',
        paragraphs: [
          "Be you. Don't create a profile pretending to be someone else — a real person, a public figure, a business you're not affiliated with, or a fictional persona designed to deceive. Your profile should represent you honestly.",
        ],
      },
      {
        title: 'No Fraud',
        iconName: 'shield',
        paragraphs: [
          "Don't use Tonyte to scam, deceive, or extract money, personal information, or anything else from other users under false pretenses — including fake ticket sales, fake giveaways, or any request for payment outside a legitimate, disclosed arrangement with an event organizer.",
        ],
      },
      {
        title: 'No Illegal Content',
        iconName: 'alert-triangle',
        paragraphs: [
          "Don't post, share, organize, or promote anything through Tonyte that's illegal where it takes place — this includes illegal goods, services, or activities of any kind. This applies to every user and every Partner organizer alike.",
        ],
      },
      {
        title: 'No Adult Content',
        iconName: 'eye-off',
        paragraphs: [
          "Tonyte's community includes users as young as 16. Sexually explicit content, nudity intended to be sexual in nature, and content soliciting sexual services are not permitted anywhere on Tonyte — profiles, chat, event listings, or anything else — regardless of your own age or the age of the person you're speaking with.",
        ],
      },
      {
        title: 'Event Safety',
        iconName: 'shield',
        paragraphs: [
          "Organizers are responsible for the events they publish being accurately described and reasonably safe for the activity involved. All users — attendees and organizers alike — are responsible for their own judgment and safety when attending an in-person event or plan. Tonyte doesn't vet organizers, attendees, or venues, so use the same common sense you'd use meeting anyone new: meet in public first, tell someone where you're going, and trust your instincts if something feels off. If a situation ever feels unsafe, leave — trust that instinct over politeness — and contact local emergency services if appropriate.",
        ],
      },
      {
        title: 'Reporting',
        iconName: 'flag',
        paragraphs: [
          "If you see something that breaks these Guidelines, report it — either the user or the event, directly from the app. Reports go to Tonyte administrators for review; you don't need to confront anyone yourself. You can also block any user at any time, which stops them from contacting or matching with you, no report required.",
        ],
      },
      {
        title: 'Enforcement',
        iconName: 'lock',
        paragraphs: [
          "When we review a report, we look at the context and decide what's proportionate. Depending on what happened, that can range from a warning, to removing specific content, to temporarily suspending an account, to permanently terminating it — as described in the Terms of Service, Section 9. Serious violations (harassment, fraud, illegal content, content endangering someone's safety) can result in immediate suspension or termination without a prior warning. Repeated minor violations can also lead to suspension or permanent termination, even where no single incident would justify it on its own.",
          'If you believe an enforcement decision was made in error, you can contact us at hello@tonyte.app to request a review.',
          "We'd rather you never need to see the enforcement side of these Guidelines — most people on Tonyte never do. Treat this community the way you'd want it to treat you, and it will. Be someone worth meeting.",
        ],
      },
    ],
  },
  de: {
    title: 'Community-Richtlinien',
    subtitle:
      'Was wir von allen erwarten, die Tonyte nutzen — in klarer Sprache. Diese Richtlinien sind Teil unserer Allgemeinen Geschäftsbedingungen.',
    iconName: 'users',
    sections: [
      {
        title: 'Einleitung',
        iconName: 'info',
        paragraphs: [
          'Tonyte hilft dir, echte Events, echte Pläne und echte Menschen zu finden, mit denen sich ein Abend lohnt. Das funktioniert nur, wenn sich die Community sicher, authentisch und willkommen fühlt — deshalb erklären wir hier in klarer Sprache, was wir von allen erwarten, die Tonyte nutzen.',
          'Diese Richtlinien sind Teil unserer Allgemeinen Geschäftsbedingungen — ein Verstoß dagegen ist ein Verstoß gegen die AGB, und die am Ende dieses Dokuments beschriebenen Durchsetzungsmaßnahmen gelten entsprechend.',
        ],
      },
      {
        title: 'Sei respektvoll',
        iconName: 'heart',
        paragraphs: [
          'Behandle andere Menschen so, wie du bei einem Event behandelt werden möchtest, auf das du dich freust. Meinungsverschiedenheiten passieren; Gemeinheit muss nicht sein. Erniedrige, bedrohe oder verärgere andere Nutzer nicht absichtlich — im Chat, auf einem Profil, bei einem Event oder überall sonst, wo Tonyte Menschen verbindet.',
        ],
      },
      {
        title: 'Sei authentisch',
        iconName: 'user-check',
        paragraphs: [
          'Dein Profil sollte dich wirklich widerspiegeln — ehrliche Fotos, korrekte Angaben und eine echte Bio. Wenn du ein Event organisierst oder besuchst, halte die geteilten Informationen wahrheitsgemäß: echte Zeiten, echte Orte, echte Absichten. Authentizität ist das, was Tonyte funktionieren lässt — bring sie in alles ein, was du postest.',
        ],
      },
      {
        title: 'Keine Belästigung',
        iconName: 'slash',
        paragraphs: [
          'Wiederholter unerwünschter Kontakt, das Verfolgen einer Person von Event zu Event, um sie in Unbehagen zu versetzen, das Senden unerwünschter expliziter Nachrichten oder das gezielte Ansprechen einer Person, nachdem sie dich gebeten hat aufzuhören, sind allesamt Belästigung und hier nicht willkommen. Wenn dich jemand blockiert oder dich bittet aufzuhören, hör auf.',
        ],
      },
      {
        title: 'Keine Hassrede',
        iconName: 'alert-triangle',
        paragraphs: [
          'Inhalte, die eine Person aufgrund von Herkunft, ethnischer Zugehörigkeit, Nationalität, Religion, Behinderung, Alter, sexueller Orientierung, Geschlechtsidentität oder eines anderen geschützten Merkmals angreifen, herabwürdigen oder zu Feindseligkeit aufstacheln, haben auf Tonyte keinen Platz — in keiner Form, sei es in Profilinhalten, im Chat, in Event-Beschreibungen oder anderswo.',
        ],
      },
      {
        title: 'Kein Spam',
        iconName: 'x-circle',
        paragraphs: [
          'Nutze Tonyte nicht, um Massenachrichten an Personen zu senden, die du nicht kennst, Chats oder Event-Listen mit sich wiederholenden Inhalten zu überfluten, oder die Plattform primär zu nutzen, um etwas zu bewerben, das nichts mit echter Vernetzung oder dem Entdecken von Events zu tun hat. Dazu zählen kommerzielle Werbung, unaufgeforderte Promotionen, Empfehlungs- oder Affiliate-Programme sowie wiederholte werbliche Nachrichten außerhalb legitimer Partner-Aktivitäten im Partner Dashboard. Wenn es sich anfühlt, als würdest du senden statt dich zu verbinden, ist es wahrscheinlich Spam.',
        ],
      },
      {
        title: 'Keine gefälschten Events',
        iconName: 'calendar',
        paragraphs: [
          'Jedes Event auf Tonyte sollte real sein — tatsächlich stattfindend, zur angegebenen Zeit am angegebenen Ort. Poste keine Events, die nicht existieren, die du nicht tatsächlich abhalten willst, oder die dazu gedacht sind, Menschen unter Vorspiegelung falscher Tatsachen irgendwohin zu locken. Dies gilt gleichermaßen für jeden Nutzer und jeden Partner-Organisator.',
        ],
      },
      {
        title: 'Keine Identitätsvortäuschung',
        iconName: 'user-x',
        paragraphs: [
          'Sei du selbst. Erstelle kein Profil, das vorgibt, jemand anderes zu sein — eine reale Person, eine Person des öffentlichen Lebens, ein Unternehmen, mit dem du nicht verbunden bist, oder eine erfundene Persona zur Täuschung. Dein Profil sollte dich ehrlich repräsentieren.',
        ],
      },
      {
        title: 'Kein Betrug',
        iconName: 'shield',
        paragraphs: [
          'Nutze Tonyte nicht, um andere Nutzer zu betrügen, zu täuschen oder ihnen unter Vorspiegelung falscher Tatsachen Geld, personenbezogene Daten oder anderes zu entlocken — einschließlich gefälschter Ticketverkäufe, gefälschter Gewinnspiele oder jeglicher Zahlungsaufforderung außerhalb einer legitimen, offengelegten Vereinbarung mit einem Event-Organisator.',
        ],
      },
      {
        title: 'Keine illegalen Inhalte',
        iconName: 'alert-triangle',
        paragraphs: [
          'Poste, teile, organisiere oder bewirb über Tonyte nichts, das an dem Ort, an dem es stattfindet, illegal ist — dies umfasst illegale Waren, Dienstleistungen oder Aktivitäten jeder Art. Dies gilt gleichermaßen für jeden Nutzer und jeden Partner-Organisator.',
        ],
      },
      {
        title: 'Keine Inhalte für Erwachsene',
        iconName: 'eye-off',
        paragraphs: [
          'Die Community von Tonyte umfasst Nutzer ab 16 Jahren. Sexuell explizite Inhalte, Nacktheit mit sexuellem Charakter und Inhalte, die sexuelle Dienstleistungen anbieten, sind auf Tonyte nirgends erlaubt — weder in Profilen, im Chat, in Event-Listen noch anderswo — unabhängig von deinem eigenen Alter oder dem Alter der Person, mit der du sprichst.',
        ],
      },
      {
        title: 'Sicherheit bei Events',
        iconName: 'shield',
        paragraphs: [
          'Organisatoren sind dafür verantwortlich, dass die von ihnen veröffentlichten Events zutreffend beschrieben und für die jeweilige Aktivität angemessen sicher sind. Alle Nutzer — Teilnehmende und Organisatoren gleichermaßen — sind für ihr eigenes Urteilsvermögen und ihre Sicherheit bei der Teilnahme an einem persönlichen Event oder Treffen selbst verantwortlich. Tonyte überprüft Organisatoren, Teilnehmende oder Veranstaltungsorte nicht, verwende daher denselben gesunden Menschenverstand wie beim Kennenlernen einer neuen Person: Triff dich zuerst an einem öffentlichen Ort, sag jemandem, wohin du gehst, und vertraue deinem Instinkt, wenn sich etwas falsch anfühlt. Wenn sich eine Situation jemals unsicher anfühlt, geh — vertraue diesem Instinkt mehr als der Höflichkeit — und kontaktiere gegebenenfalls den örtlichen Notdienst.',
        ],
      },
      {
        title: 'Melden',
        iconName: 'flag',
        paragraphs: [
          'Wenn du etwas siehst, das gegen diese Richtlinien verstößt, melde es — entweder den Nutzer oder das Event, direkt aus der App heraus. Meldungen gehen zur Prüfung an die Tonyte-Administration; du musst niemanden selbst konfrontieren. Du kannst außerdem jederzeit jeden Nutzer blockieren, was verhindert, dass er dich kontaktiert oder mit dir gematcht wird — ohne dass eine Meldung erforderlich ist.',
        ],
      },
      {
        title: 'Durchsetzung',
        iconName: 'lock',
        paragraphs: [
          'Wenn wir eine Meldung prüfen, betrachten wir den Kontext und entscheiden, was angemessen ist. Je nach Vorfall kann das von einer Verwarnung über das Entfernen bestimmter Inhalte bis zur vorübergehenden Sperrung oder dauerhaften Kündigung eines Kontos reichen — wie in den Allgemeinen Geschäftsbedingungen, Abschnitt 9, beschrieben. Schwerwiegende Verstöße (Belästigung, Betrug, illegale Inhalte, Inhalte, die die Sicherheit einer Person gefährden) können ohne vorherige Verwarnung zur sofortigen Sperrung oder Kündigung führen. Wiederholte geringfügige Verstöße können ebenfalls zu einer Sperrung oder dauerhaften Kündigung führen, auch wenn kein einzelner Vorfall dies für sich genommen rechtfertigen würde.',
          'Wenn du der Ansicht bist, dass eine Durchsetzungsentscheidung fälschlicherweise getroffen wurde, kannst du uns unter hello@tonyte.app kontaktieren, um eine Überprüfung zu beantragen.',
          'Wir würden es begrüßen, wenn du die Durchsetzungsseite dieser Richtlinien nie zu sehen bekommst — die meisten Menschen auf Tonyte erleben das auch nicht. Behandle diese Community so, wie du von ihr behandelt werden möchtest, dann wird sie es auch tun. Sei jemand, den es sich lohnt kennenzulernen.',
        ],
      },
    ],
  },
  fr: {
    title: 'Règles de la Communauté',
    subtitle:
      "Ce que nous attendons de toute personne utilisant Tonyte, en termes simples. Ces Règles font partie de nos Conditions d'Utilisation.",
    iconName: 'users',
    sections: [
      {
        title: 'Introduction',
        iconName: 'info',
        paragraphs: [
          "Tonyte existe pour vous aider à trouver de vrais événements, de vrais plans et des personnes réelles avec qui il vaut la peine de passer une soirée. Cela ne fonctionne que si la communauté se sent en sécurité, authentique et accueillante — voici donc, en termes simples, ce que nous attendons de toute personne utilisant Tonyte.",
          "Ces Règles font partie de nos Conditions d'Utilisation — les enfreindre revient à enfreindre les Conditions, et les mesures d'application décrites à la fin de ce document s'appliquent.",
        ],
      },
      {
        title: 'Soyez respectueux',
        iconName: 'heart',
        paragraphs: [
          "Traitez les autres comme vous aimeriez être traité lors d'un événement qui vous enthousiasme. Les désaccords arrivent ; la cruauté n'est pas nécessaire. Ne rabaissez, ne menacez et ne contrariez délibérément aucun autre utilisateur — que ce soit dans le chat, sur un profil, lors d'un événement, ou partout ailleurs où Tonyte connecte des personnes.",
        ],
      },
      {
        title: 'Soyez authentique',
        iconName: 'user-check',
        paragraphs: [
          "Votre profil doit refléter qui vous êtes réellement — des photos honnêtes, des informations exactes et une biographie sincère. Si vous organisez ou participez à un événement, gardez les informations que vous partagez véridiques : horaires réels, lieux réels, intentions réelles. L'authenticité est ce qui fait fonctionner Tonyte, alors apportez-la à tout ce que vous publiez.",
        ],
      },
      {
        title: 'Pas de harcèlement',
        iconName: 'slash',
        paragraphs: [
          "Les contacts non désirés répétés, le fait de suivre quelqu'un d'événement en événement pour le mettre mal à l'aise, l'envoi de messages explicites non sollicités, ou le fait de cibler quelqu'un après qu'il vous a demandé d'arrêter, constituent tous du harcèlement et ne sont pas les bienvenus ici. Si quelqu'un vous bloque ou vous demande d'arrêter, arrêtez.",
        ],
      },
      {
        title: 'Pas de discours de haine',
        iconName: 'alert-triangle',
        paragraphs: [
          "Tout contenu qui attaque, rabaisse ou incite à l'hostilité envers une personne en raison de sa race, de son origine ethnique, de sa nationalité, de sa religion, de son handicap, de son âge, de son orientation sexuelle, de son identité de genre ou de toute autre caractéristique protégée n'a pas sa place sur Tonyte, sous quelque forme que ce soit — contenu de profil, chat, description d'événement ou autre.",
        ],
      },
      {
        title: 'Pas de spam',
        iconName: 'x-circle',
        paragraphs: [
          "N'utilisez pas Tonyte pour envoyer des messages en masse à des personnes que vous ne connaissez pas, pour inonder les chats ou les listes d'événements de contenu répétitif, ou pour utiliser principalement la plateforme afin de promouvoir quelque chose qui n'a rien à voir avec le fait de se connecter réellement avec des personnes ou de découvrir des événements. Cela inclut la publicité commerciale, les promotions non sollicitées, les programmes de parrainage ou d'affiliation, et les messages promotionnels répétés en dehors d'une activité Partenaire légitime sur le Partner Dashboard. Si vous avez l'impression de diffuser plutôt que de vous connecter, il s'agit probablement de spam.",
        ],
      },
      {
        title: 'Pas de faux événements',
        iconName: 'calendar',
        paragraphs: [
          "Chaque événement sur Tonyte doit être réel, avoir réellement lieu, à l'heure et à l'endroit indiqués. Ne publiez pas d'événements qui n'existent pas, que vous n'avez pas l'intention d'organiser, ou qui sont conçus pour attirer des personnes quelque part sous de faux prétextes. Cela s'applique à chaque utilisateur et à chaque organisateur Partenaire.",
        ],
      },
      {
        title: "Pas d'usurpation d'identité",
        iconName: 'user-x',
        paragraphs: [
          "Soyez vous-même. Ne créez pas de profil prétendant être quelqu'un d'autre — une personne réelle, une personnalité publique, une entreprise à laquelle vous n'êtes pas affilié, ou un personnage fictif conçu pour tromper. Votre profil doit vous représenter honnêtement.",
        ],
      },
      {
        title: 'Pas de fraude',
        iconName: 'shield',
        paragraphs: [
          "N'utilisez pas Tonyte pour escroquer, tromper ou soutirer de l'argent, des informations personnelles ou toute autre chose à d'autres utilisateurs sous de faux prétextes — y compris de fausses ventes de billets, de faux tirages au sort, ou toute demande de paiement en dehors d'un arrangement légitime et divulgué avec un organisateur d'événement.",
        ],
      },
      {
        title: 'Pas de contenu illégal',
        iconName: 'alert-triangle',
        paragraphs: [
          "Ne publiez, ne partagez, n'organisez et ne promouvez via Tonyte rien qui soit illégal là où cela se déroule — cela inclut les biens, services ou activités illégaux de toute nature. Cela s'applique à chaque utilisateur et à chaque organisateur Partenaire.",
        ],
      },
      {
        title: 'Pas de contenu pour adultes',
        iconName: 'eye-off',
        paragraphs: [
          "La communauté de Tonyte compte des utilisateurs dès 16 ans. Le contenu sexuellement explicite, la nudité à caractère sexuel et le contenu sollicitant des services sexuels ne sont autorisés nulle part sur Tonyte — profils, chat, listes d'événements ou autre — quel que soit votre âge ou celui de la personne à qui vous parlez.",
        ],
      },
      {
        title: 'Sécurité lors des événements',
        iconName: 'shield',
        paragraphs: [
          "Les organisateurs sont responsables de la description exacte et de la sécurité raisonnable, au regard de l'activité concernée, des événements qu'ils publient. Tous les utilisateurs — participants et organisateurs — sont responsables de leur propre jugement et de leur sécurité lorsqu'ils assistent à un événement ou un plan en personne. Tonyte ne vérifie pas les organisateurs, les participants ou les lieux ; utilisez donc le même bon sens que pour rencontrer quelqu'un de nouveau : rencontrez-vous d'abord dans un lieu public, dites à quelqu'un où vous allez, et faites confiance à votre instinct si quelque chose vous semble suspect. Si une situation vous semble à un moment donné dangereuse, partez — faites davantage confiance à cet instinct qu'à la politesse — et contactez les services d'urgence locaux si nécessaire.",
        ],
      },
      {
        title: 'Signalement',
        iconName: 'flag',
        paragraphs: [
          "Si vous voyez quelque chose qui enfreint ces Règles, signalez-le — soit l'utilisateur, soit l'événement, directement depuis l'application. Les signalements sont examinés par les administrateurs de Tonyte ; vous n'avez pas besoin de confronter qui que ce soit vous-même. Vous pouvez également bloquer n'importe quel utilisateur à tout moment, ce qui l'empêche de vous contacter ou d'être mis en relation avec vous, sans qu'un signalement soit nécessaire.",
        ],
      },
      {
        title: 'Application',
        iconName: 'lock',
        paragraphs: [
          "Lorsque nous examinons un signalement, nous tenons compte du contexte et décidons de la mesure proportionnée. Selon les circonstances, cela peut aller d'un avertissement, à la suppression d'un contenu spécifique, à la suspension temporaire d'un compte, jusqu'à sa résiliation définitive — comme décrit dans les Conditions d'Utilisation, Section 9. Les infractions graves (harcèlement, fraude, contenu illégal, contenu mettant en danger la sécurité d'une personne) peuvent entraîner une suspension ou une résiliation immédiate, sans avertissement préalable. Des infractions mineures répétées peuvent également entraîner une suspension ou une résiliation définitive, même lorsqu'aucun incident isolé ne le justifierait à lui seul.",
          'Si vous estimez qu’une décision d’application a été prise par erreur, vous pouvez nous contacter à hello@tonyte.app pour demander un réexamen.',
          "Nous préférerions que vous n'ayez jamais besoin de voir le volet application de ces Règles — la plupart des utilisateurs de Tonyte n'en ont d'ailleurs jamais besoin. Traitez cette communauté comme vous aimeriez qu'elle vous traite, et elle le fera. Soyez quelqu'un qui vaut la peine d'être rencontré.",
        ],
      },
    ],
  },
  es: {
    title: 'Normas de la Comunidad',
    subtitle:
      'Lo que esperamos de todas las personas que usan Tonyte, en un lenguaje claro. Estas Normas forman parte de nuestros Términos de Servicio.',
    iconName: 'users',
    sections: [
      {
        title: 'Introducción',
        iconName: 'info',
        paragraphs: [
          'Tonyte existe para ayudarte a encontrar eventos reales, planes reales y personas reales con las que merezca la pena pasar una velada. Eso solo funciona si la comunidad se siente segura, auténtica y acogedora — así que esto es, en un lenguaje claro, lo que esperamos de todas las personas que usan Tonyte.',
          'Estas Normas forman parte de nuestros Términos de Servicio — incumplirlas es incumplir los Términos, y se aplican las medidas de aplicación descritas al final de este documento.',
        ],
      },
      {
        title: 'Sé respetuoso',
        iconName: 'heart',
        paragraphs: [
          'Trata a los demás como te gustaría que te trataran en un evento que te ilusiona. Los desacuerdos ocurren; la crueldad no es necesaria. No humilles, amenaces ni molestes deliberadamente a otros usuarios — en el chat, en un perfil, en un evento o en cualquier otro lugar donde Tonyte conecte a las personas.',
        ],
      },
      {
        title: 'Sé auténtico',
        iconName: 'user-check',
        paragraphs: [
          'Tu perfil debe reflejar quién eres realmente — fotos honestas, datos precisos y una biografía genuina. Si organizas o asistes a un evento, mantén la información que compartes veraz: horarios reales, ubicaciones reales, intenciones reales. La autenticidad es lo que hace que Tonyte funcione, así que aplícala a todo lo que publiques.',
        ],
      },
      {
        title: 'Sin acoso',
        iconName: 'slash',
        paragraphs: [
          'El contacto no deseado y repetido, seguir a alguien de evento en evento para incomodarlo, enviar mensajes explícitos no solicitados, o dirigirte a alguien después de que te haya pedido que pares, son todo formas de acoso y no tienen cabida aquí. Si alguien te bloquea o te pide que pares, para.',
        ],
      },
      {
        title: 'Sin discurso de odio',
        iconName: 'alert-triangle',
        paragraphs: [
          'El contenido que ataca, humilla o incita a la hostilidad hacia alguien por su raza, origen étnico, nacionalidad, religión, discapacidad, edad, orientación sexual, identidad de género o cualquier otra característica protegida no tiene cabida en Tonyte, en ninguna forma — contenido de perfil, chat, descripciones de eventos o cualquier otro medio.',
        ],
      },
      {
        title: 'Sin spam',
        iconName: 'x-circle',
        paragraphs: [
          'No uses Tonyte para enviar mensajes masivos a personas que no conoces, inundar chats o listados de eventos con contenido repetitivo, o utilizar la plataforma principalmente para promocionar algo no relacionado con conectar genuinamente con personas o descubrir eventos. Esto incluye publicidad comercial, promociones no solicitadas, esquemas de referidos o afiliados, y mensajes promocionales repetidos fuera de la actividad legítima de Socio en el Partner Dashboard. Si sientes que estás difundiendo en lugar de conectar, probablemente sea spam.',
        ],
      },
      {
        title: 'Sin eventos falsos',
        iconName: 'calendar',
        paragraphs: [
          'Todo evento en Tonyte debe ser real, que realmente vaya a ocurrir, en la hora y el lugar indicados. No publiques eventos que no existen, que no tienes intención de celebrar, o que están diseñados para atraer a personas a algún lugar con falsos pretextos. Esto se aplica por igual a cada usuario y cada organizador Socio.',
        ],
      },
      {
        title: 'Sin suplantación de identidad',
        iconName: 'user-x',
        paragraphs: [
          'Sé tú mismo. No crees un perfil haciéndote pasar por otra persona — una persona real, una figura pública, una empresa con la que no estás afiliado, o una persona ficticia diseñada para engañar. Tu perfil debe representarte honestamente.',
        ],
      },
      {
        title: 'Sin fraude',
        iconName: 'shield',
        paragraphs: [
          'No uses Tonyte para estafar, engañar o sustraer dinero, información personal o cualquier otra cosa a otros usuarios bajo falsos pretextos — incluidas ventas falsas de entradas, sorteos falsos, o cualquier solicitud de pago fuera de un acuerdo legítimo y divulgado con un organizador de eventos.',
        ],
      },
      {
        title: 'Sin contenido ilegal',
        iconName: 'alert-triangle',
        paragraphs: [
          'No publiques, compartas, organices ni promociones a través de Tonyte nada que sea ilegal en el lugar donde tiene lugar — esto incluye bienes, servicios o actividades ilegales de cualquier tipo. Esto se aplica por igual a cada usuario y cada organizador Socio.',
        ],
      },
      {
        title: 'Sin contenido para adultos',
        iconName: 'eye-off',
        paragraphs: [
          'La comunidad de Tonyte incluye usuarios a partir de 16 años. El contenido sexualmente explícito, la desnudez de carácter sexual y el contenido que solicita servicios sexuales no están permitidos en ningún lugar de Tonyte — perfiles, chat, listados de eventos o cualquier otro medio — independientemente de tu propia edad o la edad de la persona con la que hables.',
        ],
      },
      {
        title: 'Seguridad en los eventos',
        iconName: 'shield',
        paragraphs: [
          'Los organizadores son responsables de que los eventos que publican estén descritos con precisión y sean razonablemente seguros para la actividad implicada. Todos los usuarios — asistentes y organizadores por igual — son responsables de su propio criterio y seguridad al asistir a un evento o plan presencial. Tonyte no verifica a los organizadores, asistentes ni lugares, así que usa el mismo sentido común que emplearías al conocer a alguien nuevo: reúnete primero en un lugar público, dile a alguien adónde vas y confía en tu instinto si algo no te parece bien. Si en algún momento una situación te parece insegura, vete — confía más en ese instinto que en la cortesía — y contacta con los servicios de emergencia locales si es necesario.',
        ],
      },
      {
        title: 'Denuncias',
        iconName: 'flag',
        paragraphs: [
          'Si ves algo que infringe estas Normas, denúncialo — ya sea al usuario o al evento, directamente desde la aplicación. Las denuncias se envían a los administradores de Tonyte para su revisión; no necesitas confrontar a nadie tú mismo. También puedes bloquear a cualquier usuario en cualquier momento, lo que impide que te contacte o coincida contigo, sin necesidad de denuncia.',
        ],
      },
      {
        title: 'Aplicación',
        iconName: 'lock',
        paragraphs: [
          'Cuando revisamos una denuncia, analizamos el contexto y decidimos qué es proporcionado. Según lo ocurrido, esto puede ir desde una advertencia, pasando por la eliminación de contenido específico, hasta la suspensión temporal de una cuenta o su cancelación permanente — como se describe en los Términos de Servicio, Sección 9. Las infracciones graves (acoso, fraude, contenido ilegal, contenido que ponga en peligro la seguridad de alguien) pueden dar lugar a la suspensión o cancelación inmediata sin advertencia previa. Las infracciones leves repetidas también pueden dar lugar a la suspensión o cancelación permanente, incluso cuando ningún incidente aislado la justificaría por sí solo.',
          'Si crees que una decisión de aplicación se tomó por error, puedes contactarnos en hello@tonyte.app para solicitar una revisión.',
          'Preferiríamos que nunca tuvieras que ver el lado de aplicación de estas Normas — la mayoría de las personas en Tonyte nunca lo hace. Trata a esta comunidad como te gustaría que te tratara a ti, y lo hará. Sé alguien que merezca la pena conocer.',
        ],
      },
    ],
  },
  ru: {
    title: 'Правила сообщества',
    subtitle:
      'Чего мы ожидаем от всех, кто пользуется Tonyte, простыми словами. Эти Правила являются частью наших Условий использования.',
    iconName: 'users',
    sections: [
      {
        title: 'Введение',
        iconName: 'info',
        paragraphs: [
          'Tonyte помогает находить настоящие события, настоящие планы и настоящих людей, с которыми стоит провести вечер. Это работает только тогда, когда сообщество чувствует себя в безопасности, ощущает подлинность и радушие — поэтому вот, простыми словами, чего мы ожидаем от всех, кто пользуется Tonyte.',
          'Эти Правила являются частью наших Условий использования — их нарушение является нарушением Условий, и к нему применяются меры, описанные в конце этого документа.',
        ],
      },
      {
        title: 'Будьте уважительны',
        iconName: 'heart',
        paragraphs: [
          'Относитесь к другим людям так, как хотели бы, чтобы относились к вам на мероприятии, которое вас радует. Разногласия случаются; жестокость — нет необходимости. Не унижайте, не угрожайте и не расстраивайте намеренно других пользователей — в чате, в профиле, на мероприятии или где-либо ещё, где Tonyte соединяет людей.',
        ],
      },
      {
        title: 'Будьте настоящими',
        iconName: 'user-check',
        paragraphs: [
          'Ваш профиль должен отражать вас настоящего — честные фотографии, точные данные и искреннее описание. Если вы организуете мероприятие или посещаете его, указывайте достоверную информацию: настоящее время, настоящее место, настоящие намерения. Подлинность — это то, что делает Tonyte работающим сервисом, поэтому привносите её во всё, что вы публикуете.',
        ],
      },
      {
        title: 'Никаких домогательств',
        iconName: 'slash',
        paragraphs: [
          'Повторяющиеся нежелательные контакты, преследование человека от мероприятия к мероприятию с целью заставить его чувствовать себя некомфортно, отправка нежелательных откровенных сообщений или преследование человека после того, как он попросил вас остановиться, — всё это является домогательством и здесь недопустимо. Если кто-то заблокировал вас или попросил остановиться — остановитесь.',
        ],
      },
      {
        title: 'Никаких высказываний на почве ненависти',
        iconName: 'alert-triangle',
        paragraphs: [
          'Контент, который нападает, унижает или разжигает враждебность по отношению к человеку на основании расы, этнической принадлежности, национального происхождения, религии, инвалидности, возраста, сексуальной ориентации, гендерной идентичности или любой другой защищённой характеристики, не допускается на Tonyte ни в какой форме — будь то содержимое профиля, чат, описание мероприятия или что-либо ещё.',
        ],
      },
      {
        title: 'Никакого спама',
        iconName: 'x-circle',
        paragraphs: [
          'Не используйте Tonyte для массовой рассылки сообщений незнакомым людям, для заполнения чатов или списков мероприятий повторяющимся контентом, а также для использования платформы преимущественно с целью продвижения чего-либо, не связанного с настоящим общением или поиском мероприятий. Сюда относятся коммерческая реклама, незапрошенные акции, реферальные или партнёрские программы, а также повторяющиеся рекламные сообщения за пределами законной деятельности Партнёра в Partner Dashboard. Если вам кажется, что вы транслируете, а не общаетесь, — вероятно, это спам.',
        ],
      },
      {
        title: 'Никаких фиктивных мероприятий',
        iconName: 'calendar',
        paragraphs: [
          'Каждое мероприятие на Tonyte должно быть настоящим — действительно происходящим в указанное время и в указанном месте. Не публикуйте мероприятия, которые не существуют, которые вы не намерены проводить, или которые предназначены для того, чтобы заманить людей куда-либо под ложным предлогом. Это в равной мере относится к каждому пользователю и каждому организатору-Партнёру.',
        ],
      },
      {
        title: 'Никакого выдавания себя за другого',
        iconName: 'user-x',
        paragraphs: [
          'Будьте собой. Не создавайте профиль, выдавая себя за кого-то другого — реального человека, публичную личность, компанию, с которой вы не связаны, или вымышленную личность, созданную для обмана. Ваш профиль должен честно представлять вас.',
        ],
      },
      {
        title: 'Никакого мошенничества',
        iconName: 'shield',
        paragraphs: [
          'Не используйте Tonyte для обмана, введения в заблуждение или получения денег, персональных данных или чего-либо ещё от других пользователей под ложным предлогом — включая продажу поддельных билетов, фиктивные розыгрыши или любые запросы платежей вне рамок законной, раскрытой договорённости с организатором мероприятия.',
        ],
      },
      {
        title: 'Никакого противоправного контента',
        iconName: 'alert-triangle',
        paragraphs: [
          'Не публикуйте, не распространяйте, не организуйте и не продвигайте через Tonyte ничего, что является противоправным в месте, где это происходит, — включая любые незаконные товары, услуги или деятельность. Это в равной мере относится к каждому пользователю и каждому организатору-Партнёру.',
        ],
      },
      {
        title: 'Никакого контента для взрослых',
        iconName: 'eye-off',
        paragraphs: [
          'Сообщество Tonyte включает пользователей от 16 лет. Сексуально откровенный контент, обнажённость сексуального характера и контент, предлагающий сексуальные услуги, не допускаются нигде на Tonyte — ни в профилях, ни в чате, ни в списках мероприятий, ни где-либо ещё — независимо от вашего возраста или возраста собеседника.',
        ],
      },
      {
        title: 'Безопасность на мероприятиях',
        iconName: 'shield',
        paragraphs: [
          'Организаторы несут ответственность за то, чтобы публикуемые ими мероприятия были точно описаны и разумно безопасны с учётом характера деятельности. Все пользователи — как участники, так и организаторы — несут ответственность за собственное благоразумие и безопасность при посещении очного мероприятия или встречи. Tonyte не проверяет организаторов, участников или площадки, поэтому руководствуйтесь тем же здравым смыслом, что и при знакомстве с новым человеком: встречайтесь сначала в общественном месте, сообщите кому-нибудь, куда вы идёте, и доверяйте своей интуиции, если что-то кажется не так. Если ситуация когда-либо покажется небезопасной, уходите — доверяйте этому инстинкту больше, чем вежливости, — и при необходимости обращайтесь в местные экстренные службы.',
        ],
      },
      {
        title: 'Жалобы',
        iconName: 'flag',
        paragraphs: [
          'Если вы видите что-то, что нарушает эти Правила, сообщите об этом — о пользователе или о мероприятии, прямо из приложения. Жалобы направляются администраторам Tonyte для рассмотрения; вам не нужно самостоятельно вступать в конфронтацию с кем-либо. Вы также можете заблокировать любого пользователя в любой момент, что не позволит ему связаться с вами или получить совпадение с вами, без необходимости подавать жалобу.',
        ],
      },
      {
        title: 'Меры воздействия',
        iconName: 'lock',
        paragraphs: [
          'Рассматривая жалобу, мы учитываем контекст и определяем соразмерную меру. В зависимости от произошедшего это может быть предупреждение, удаление конкретного контента, временная блокировка учётной записи или её окончательное прекращение — как описано в Условиях использования, раздел 9. Серьёзные нарушения (домогательства, мошенничество, противоправный контент, контент, угрожающий чьей-либо безопасности) могут повлечь немедленную блокировку или прекращение действия учётной записи без предварительного предупреждения. Повторяющиеся незначительные нарушения также могут привести к блокировке или окончательному прекращению действия учётной записи, даже если ни один отдельный случай сам по себе не оправдывал бы этого.',
          'Если вы считаете, что решение о применении мер было принято ошибочно, вы можете связаться с нами по адресу hello@tonyte.app, чтобы запросить пересмотр.',
          'Мы бы предпочли, чтобы вам никогда не пришлось столкнуться с мерами воздействия по этим Правилам — большинство людей на Tonyte никогда с этим не сталкиваются. Относитесь к этому сообществу так, как хотели бы, чтобы относились к вам, — и оно ответит тем же. Будьте тем, с кем стоит познакомиться.',
        ],
      },
    ],
  },
};
